import { urlToString, areEqual } from "Shared/url";
import { componentNameIsCheckoutPage } from "../../Checkout/Pages/Checkout/current-page-is-checkout";
import localForage from "localforage";
import { supportAsyncStorage } from "Shared/device-type";
import { componentIsFavorites, componentIsPantries, } from "Shared/ListItem/current-page-is";
import { componentNameIsCustomProductsPage } from "../../CustomProducts/current-page-is-custom-products";
import { componentNameIsMyMenigoPage } from '../../MyMenigo/Blocks/DeliveryBlock/current-page-is-my-menigo-page';
const maxCacheSize = 50;
const requestTimeout = 100;
const storage = localForage.createInstance({
    storeName: "cache",
});
let allCachedUrls = null;
// We keep a small cache in memory of full pages so that we can respond quicker
// for the pages the user just viewed. This is mainly because when you go back
// in the browser history, the browser will restore scroll position and if we
// don't respond fast enough from the cache we haven't rendered the page when
// the browser restores scroll position. Which means that the scroll position
// will first jump, then we render, then the scroll position changes again.
const fastCache = {};
let fastCacheUrls = [];
const MAX_FAST_CACHE_SIZE = 10;
export function isCacheItem(item) {
    return !!(item && item.url && item.$cache && item.componentName && item.meta);
}
export function get(url) {
    if (!supportAsyncStorage) {
        return Promise.resolve(null);
    }
    if (fastCache[url]) {
        const item = fastCache[url];
        if (isNonCachablePage(item)) {
            console.info('NOT Loading', url, 'from fast cache');
            return Promise.resolve(null);
        }
        else {
            console.info('Loading', url, 'from fast cache');
            // If you request something from the fast queue
            // we want to update the priority list so we don't
            // drop something from the fast cache that was recently used
            placeFirstInFastCache(fastCache[url]);
            return Promise.resolve(fastCache[url]);
        }
    }
    if (allCachedUrls && allCachedUrls.indexOf(url) === -1) {
        const item = findInFastCache(url);
        if (item && isNonCachablePage(item)) {
            return Promise.resolve(null);
        }
        return Promise.resolve(item);
    }
    return Promise.race([
        timeout(),
        storage.getItem(url).then((item) => {
            if (item) {
                if (isNonCachablePage(item)) {
                    return null;
                }
                addToFastCacheIfFullItem(item);
            }
            return item;
        }, () => {
            // If the storage is full, we just act like we don't have anything
            return null;
        }),
    ])
        .then((cacheItem) => {
        if (cacheItem && cacheItem.timeout) {
            console.debug("Timing out cache storage request because it took more than " +
                requestTimeout +
                "ms");
            return null;
        }
        if (!cacheItem) {
            const item = findInFastCache(url);
            if (item && isNonCachablePage(item)) {
                return null;
            }
            return item;
        }
        return cacheItem;
    });
}
function findInFastCache(url) {
    for (let i = fastCacheUrls.length - 1; i >= 0; i--) {
        const partial = findIn(fastCache[fastCacheUrls[i]], url);
        if (partial) {
            return partial;
        }
    }
    return null;
}
function findIn(cacheItem, url) {
    if (Array.isArray(cacheItem)) {
        for (let i = 0; i < cacheItem.length; i++) {
            const item = cacheItem[i];
            if (isCacheItem(item) && areEqual(urlToString(item.url), url)) {
                return item;
            }
            // A partial never contains cache items
            if (!isCacheItem(item) || item.$cache !== "partial") {
                const deep = findIn(item, url);
                if (deep) {
                    return deep;
                }
            }
        }
    }
    else if (cacheItem instanceof Object) {
        if (isCacheItem(cacheItem) && areEqual(urlToString(cacheItem.url), url)) {
            return cacheItem;
        }
        // A partial never contains cache items
        if (!isCacheItem(cacheItem) || cacheItem.$cache !== "partial") {
            const keys = Object.keys(cacheItem);
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                const item = cacheItem[key];
                if (isCacheItem(item) && areEqual(urlToString(item.url), url)) {
                    return item;
                }
                const deep = findIn(item, url);
                if (deep) {
                    return deep;
                }
            }
        }
    }
    return null;
}
function timeout() {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve({ timeout: true });
        }, requestTimeout);
    });
}
function placeFirstInFastCache(item) {
    const url = urlToString(item.url);
    fastCacheUrls = fastCacheUrls.filter((cachedUrl) => cachedUrl !== url);
    fastCacheUrls.push(url);
}
function addToFastCacheIfFullItem(item) {
    if (item.$cache === "full") {
        const url = urlToString(item.url);
        if (fastCacheUrls.length > MAX_FAST_CACHE_SIZE) {
            const oldestUrlInFastCache = fastCacheUrls.shift();
            console.info("Dropping", oldestUrlInFastCache, "from fast cache");
            delete fastCache[oldestUrlInFastCache];
        }
        placeFirstInFastCache(item);
        fastCache[url] = item;
    }
}
function isNonCachablePage(item) {
    const doNotCache = [
        componentNameIsCheckoutPage(item.componentName),
        componentIsFavorites(item.componentName),
        componentIsPantries(item.componentName),
        componentNameIsCustomProductsPage(item.componentName),
        componentNameIsMyMenigoPage(item.componentName)
    ];
    return doNotCache.some(Boolean);
}
export function set(item, fastCacheOnly = false) {
    if (!supportAsyncStorage) {
        return Promise.resolve(null);
    }
    if (!item.componentName || isNonCachablePage(item)) {
        console.info("Not caching page");
        return Promise.resolve(null);
    }
    const url = urlToString(item.url);
    addToFastCacheIfFullItem(item);
    if (fastCacheOnly) {
        return Promise.resolve(null);
    }
    storage.getItem(url).then((existing) => {
        if (!existing || existing.$cache === "partial" || item.$cache === "full") {
            onCacheSet(url);
            // We don't want to remove unserializable properties from the object
            // because the sender might use them after calling this method
            const serializable = {};
            Object.keys(item).forEach((property) => {
                // Because we can't serialize functions
                if (!(item[property] instanceof Function)) {
                    serializable[property] = item[property];
                }
            });
            return storage
                .setItem(url, serializable)
                .catch((e) => console.error("Error storing in item cache", url, e));
        }
        else {
            console.info("Not storing partial cache for " +
                url +
                " because there is already full content cached");
        }
        return Promise.resolve(null);
    });
    // return Promise.resolve(null);
}
export function clear(url) {
    const index = fastCacheUrls.findIndex((u) => u === url);
    if (index !== -1) {
        fastCacheUrls.splice(index, 1);
        delete fastCache[url];
    }
    return storage.removeItem(url);
}
export function nuke() {
    Object.keys(fastCache).forEach((url) => delete fastCache[url]);
    fastCacheUrls = [];
    return Promise.race([timeout(), storage.clear().catch(() => null)]);
}
const urlsByDateKey = "urls-by-date";
let flushTimer;
let pendingNewUrls = [];
function onCacheSet(url) {
    if (!pendingNewUrls.includes(url)) {
        pendingNewUrls.unshift(url);
        clearTimeout(flushTimer);
        flushTimer = setTimeout(saveCachedUrlsList, 100);
    }
}
storage
    .getItem(urlsByDateKey)
    .then((urls) => (allCachedUrls = urls || []));
function saveCachedUrlsList() {
    storage.getItem(urlsByDateKey).then((urls) => {
        urls = urls || [];
        urls = urls.filter((url) => !pendingNewUrls.includes(url));
        urls = pendingNewUrls.concat(urls);
        pendingNewUrls = [];
        if (urls.length > maxCacheSize) {
            const urlsToRemove = urls.slice(maxCacheSize - 1);
            urls = urls.slice(0, maxCacheSize - 1);
            urlsToRemove.forEach((url) => {
                storage.removeItem(url);
                console.info("Removing", url, "from the item cache");
            });
        }
        allCachedUrls = urls;
        storage.setItem(urlsByDateKey, urls).then(() => {
            if (pendingNewUrls.length > 0) {
                clearTimeout(flushTimer);
                flushTimer = setTimeout(saveCachedUrlsList, 100);
            }
        });
    });
}
